import { Box, Chip, Paper, Skeleton, Typography } from '@mui/material'
import { Campaign, Insights } from '@mui/icons-material'
import { useEffect, useState } from 'react'
import { CarnivoreAlert } from '../../_types/alert.type'
import { AlertService } from '../../services/alert.service'
import { useError } from '../../context/error'
import { useLiveAlert } from '../../context/live-alert.context'
import ViewAlertModal from '../modals/view-alert.modal'

const HERO_PULSE_MS = 1800

function timeAgo(ts: number): string {
  const diff = Date.now() - ts
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  return `${d}d ago`
}

export default function AlertCenter() {
  const { setError } = useError()
  const alertService = new AlertService(setError)
  const { liveAlert } = useLiveAlert()

  const [alerts, setAlerts] = useState<CarnivoreAlert[]>([])
  const [loading, setLoading] = useState(true)
  const [pulse, setPulse] = useState(false)
  const [modalAlert, setModalAlert] = useState<CarnivoreAlert | null>(null)

  useEffect(() => {
    let cancelled = false
    alertService.searchAlerts(1, undefined, undefined, 5).then(res => {
      if (cancelled) return
      if (res?.alerts?.docs) setAlerts(res.alerts.docs)
      setLoading(false)
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!liveAlert) return
    setAlerts(prev => {
      if (prev.some(a => a.id === liveAlert.id)) return prev
      return [liveAlert, ...prev].slice(0, 5)
    })
    setPulse(true)
    const id = window.setTimeout(() => setPulse(false), HERO_PULSE_MS)
    return () => window.clearTimeout(id)
  }, [liveAlert])

  const hero = alerts[0]
  const tape = alerts.slice(1, 5)

  return (
    <Box>
      {loading ? (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Skeleton variant='text' width='40%' />
          <Skeleton variant='text' width='80%' height={48} />
          <Skeleton variant='text' width='100%' />
          <Skeleton variant='text' width='90%' />
        </Paper>
      ) : hero ? (
        <Paper
          elevation={3}
          onClick={() => setModalAlert(hero)}
          sx={{
            p: 3,
            cursor: 'pointer',
            position: 'relative',
            border: '2px solid transparent',
            transition: 'border-color 0.3s, box-shadow 0.3s',
            ...(pulse && {
              borderColor: 'primary.main',
              boxShadow: '0 0 0 4px rgba(25, 118, 210, 0.18)'
            }),
            '&:hover': { boxShadow: 6 }
          }}
        >
          <Box display='flex' alignItems='center' gap={1} mb={1}>
            {hero.alertType === 'alert' ? <Campaign color='primary' /> : <Insights color='primary' />}
            <Chip
              label={hero.alertType === 'alert' ? 'ALERT' : 'UPDATE'}
              size='small'
              color={hero.alertType === 'alert' ? 'primary' : 'default'}
            />
            {hero.tag && <Chip label={hero.tag} size='small' variant='outlined' />}
            <Box flex={1} />
            <Typography variant='caption' color='text.secondary'>
              {timeAgo(hero.timeStamp)}
            </Typography>
          </Box>
          <Typography variant='h5' fontWeight={700} gutterBottom>
            {hero.subject}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {hero.short}
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant='body2' color='text.secondary'>No alerts yet.</Typography>
        </Paper>
      )}

      <Box mt={1}>
        {loading
          ? Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} variant='rectangular' height={44} sx={{ my: 0.5, borderRadius: 1 }} />
            ))
          : tape.map(a => (
              <Paper
                key={a.id}
                elevation={1}
                onClick={() => setModalAlert(a)}
                sx={{
                  px: 2, py: 1, my: 0.5, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 1,
                  transition: 'background-color 0.2s',
                  '&:hover': { backgroundColor: 'action.hover' }
                }}
              >
                <Chip
                  label={a.alertType === 'alert' ? 'ALERT' : 'UPDATE'}
                  size='small'
                  color={a.alertType === 'alert' ? 'primary' : 'default'}
                />
                <Typography variant='body2' fontWeight={500} noWrap sx={{ flex: 1 }}>
                  {a.subject}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {timeAgo(a.timeStamp)}
                </Typography>
              </Paper>
            ))}
      </Box>

      {modalAlert && (
        <ViewAlertModal
          open={!!modalAlert}
          onClose={() => setModalAlert(null)}
          alert={modalAlert}
          size='lg'
        />
      )}
    </Box>
  )
}
